import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { buildSmsUrl } from "../lib/sms";
import HuntingSeasons from "../components/HuntingSeasons";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Target, Plus, Trash2, Edit, TrendingUp, AlertTriangle, Phone, Send, Lock, Unlock, ChevronDown, ChevronUp } from "lucide-react";

const QuotaPage = () => {
  const { token, hasRole } = useAuth();
  const [quotas, setQuotas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotaToDelete, setQuotaToDelete] = useState(null);
  const [editingQuota, setEditingQuota] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const SHOW_CLOSED_KEY = "quota_show_closed_v1";
  const [showClosed, setShowClosed] = useState(() => {
    try {
      const raw = localStorage.getItem(SHOW_CLOSED_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_CLOSED_KEY, JSON.stringify(showClosed));
    } catch { /* ignore */ }
  }, [showClosed]);
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [smsNotifyData, setSmsNotifyData] = useState(null);
  
  const currentYear = new Date().getFullYear();
  const [formData, setFormData] = useState({
    kategorija: "",
    kategorija_naziv: "",
    podkategorija: "",
    podkategorija_naziv: "",
    plan: 0,
    odstreljeno: 0,
    sezona: `${currentYear}/${currentYear + 1}`,
    opis: ""
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [quotasRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/quotas`, { headers }),
        axios.get(`${API}/animal-categories`, { headers })
      ]);
      setQuotas(quotasRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Napaka pri nalaganju podatkov");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingQuota) {
        await axios.put(`${API}/quotas/${editingQuota.id}`, {
          plan: formData.plan,
          odstreljeno: formData.odstreljeno,
          opis: formData.opis
        }, { headers });
        toast.success("Kvota posodobljena");
      } else {
        await axios.post(`${API}/quotas`, formData, { headers });
        toast.success("Kvota dodana");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  const handleDelete = async (id) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/quotas/${id}`, { headers });
      toast.success("Kvota izbrisana");
      setDeleteDialogOpen(false);
      setQuotaToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Napaka pri brisanju");
    }
  };

  const openDeleteDialog = (quota) => {
    setQuotaToDelete(quota);
    setDeleteDialogOpen(true);
  };

  // Toggle hunting open/closed for a specific quota
  const handleToggleStatus = async (quota, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.put(`${API}/quotas/${quota.id}/status`, { status: newStatus }, { headers });
      const action = newStatus === "odprto" ? "odprt" : "zaprt";
      toast.success(`Odstrel ${action}: ${quota.podkategorija_naziv}`);
      fetchData();
      // Offer SMS notification
      if (res.data.sms_besedilo && res.data.phones?.length > 0) {
        setSmsNotifyData({ sms_besedilo: res.data.sms_besedilo, phones: res.data.phones });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri spremembi statusa");
    }
  };

  // SMS helpers - iOS-safe: use <a href="sms:..."> instead of window.location.href
  // (iOS Safari blocks programmatic sms: navigation unless it's a direct user gesture)
  const smsUrlAll = smsNotifyData
    ? buildSmsUrl(smsNotifyData.phones, smsNotifyData.sms_besedilo)
    : "";
  const smsUrlFirst = smsNotifyData && smsNotifyData.phones.length > 0
    ? buildSmsUrl([smsNotifyData.phones[0]], smsNotifyData.sms_besedilo)
    : "";

  const resetForm = () => {
    setFormData({
      kategorija: "",
      kategorija_naziv: "",
      podkategorija: "",
      podkategorija_naziv: "",
      plan: 0,
      odstreljeno: 0,
      sezona: `${currentYear}/${currentYear + 1}`,
      opis: ""
    });
    setSelectedCategory("");
    setSelectedSubcategory("");
    setEditingQuota(null);
  };

  const openEditDialog = (quota) => {
    setEditingQuota(quota);
    setFormData({
      kategorija: quota.kategorija,
      kategorija_naziv: quota.kategorija_naziv,
      podkategorija: quota.podkategorija,
      podkategorija_naziv: quota.podkategorija_naziv,
      plan: quota.plan,
      odstreljeno: quota.odstreljeno,
      sezona: quota.sezona || `${currentYear}/${currentYear + 1}`,
      opis: quota.opis || ""
    });
    setSelectedCategory(quota.kategorija);
    setSelectedSubcategory(quota.podkategorija);
    setDialogOpen(true);
  };

  const handleCategorySelect = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      setSelectedCategory(categoryId);
      setSelectedSubcategory("");
      setFormData({
        ...formData,
        kategorija: categoryId,
        kategorija_naziv: category.naziv,
        podkategorija: "",
        podkategorija_naziv: ""
      });
    }
  };

  const handleSubcategorySelect = (subcategoryId) => {
    const category = categories.find(c => c.id === selectedCategory);
    const subcategory = category?.podkategorije.find(s => s.id === subcategoryId);
    if (subcategory) {
      setSelectedSubcategory(subcategoryId);
      setFormData({
        ...formData,
        podkategorija: subcategoryId,
        podkategorija_naziv: subcategory.naziv
      });
    }
  };

  const getQuotaPercentage = (quota) => {
    if (quota.plan === 0) return 0;
    return Math.min(Math.round((quota.odstreljeno / quota.plan) * 100), 100);
  };

  const getQuotaColor = (percentage) => {
    if (percentage <= 25) return "bg-green-500";
    if (percentage <= 50) return "bg-yellow-500";
    if (percentage <= 75) return "bg-orange-500";
    return "bg-red-500";
  };

  const getQuotaBadge = (percentage) => {
    if (percentage <= 25) return { label: "Nizka", className: "bg-green-100 text-green-800" };
    if (percentage <= 50) return { label: "Srednja", className: "bg-yellow-100 text-yellow-800" };
    if (percentage <= 75) return { label: "Visoka", className: "bg-orange-100 text-orange-800" };
    return { label: "Kritično", className: "bg-red-100 text-red-800" };
  };

  // Group quotas by category
  const groupedQuotas = quotas.reduce((acc, quota) => {
    const key = quota.kategorija;
    if (!acc[key]) {
      acc[key] = {
        naziv: quota.kategorija_naziv,
        items: []
      };
    }
    acc[key].items.push(quota);
    return acc;
  }, {});

  // Sort within each category: OPEN (still has plan capacity and not manually closed) first, then CLOSED
  Object.values(groupedQuotas).forEach(cat => {
    cat.items.sort((a, b) => {
      const isQuotaClosed = (q) =>
        q.odstrel_status === "zaprto" ||
        ((q.plan || 0) > 0 && (q.odstreljeno || 0) >= (q.plan || 0));
      const aClosed = isQuotaClosed(a);
      const bClosed = isQuotaClosed(b);
      if (aClosed !== bClosed) return aClosed ? 1 : -1; // open first
      // Same status -> preserve previous order (by name)
      return (a.podkategorija_naziv || "").localeCompare(b.podkategorija_naziv || "");
    });
  });

  // Calculate totals per category
  const getCategoryTotals = (items) => {
    const plan = items.reduce((sum, q) => sum + q.plan, 0);
    const odstreljeno = items.reduce((sum, q) => sum + q.odstreljeno, 0);
    const percentage = plan > 0 ? Math.round((odstreljeno / plan) * 100) : 0;
    return { plan, odstreljeno, percentage };
  };

  const canManage = hasRole(["predsednik", "gospodar"]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="quota-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
            <Target className="h-6 w-6 sm:h-8 sm:w-8" />
            Plan odstrela
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Letni plan odstrela divjadi
          </p>
        </div>
        
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-quota-btn" className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Dodaj kvoto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md mx-4">
              <DialogHeader>
                <DialogTitle>{editingQuota ? "Uredi kvoto" : "Nova kvota"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!editingQuota && (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Kategorija divjadi</Label>
                      <Select onValueChange={handleCategorySelect} value={selectedCategory}>
                        <SelectTrigger>
                          <SelectValue placeholder="Izberite kategorijo" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.naziv}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedCategory && (
                      <div className="space-y-1">
                        <Label className="text-xs">Podkategorija</Label>
                        <Select onValueChange={handleSubcategorySelect} value={selectedSubcategory}>
                          <SelectTrigger>
                            <SelectValue placeholder="Izberite podkategorijo" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories
                              .find(c => c.id === selectedCategory)
                              ?.podkategorije.map((sub) => (
                                <SelectItem key={sub.id} value={sub.id}>
                                  {sub.naziv}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Plan</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.plan}
                      onChange={(e) => setFormData({ ...formData, plan: parseInt(e.target.value) || 0 })}
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Odstreljeno</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.odstreljeno}
                      onChange={(e) => setFormData({ ...formData, odstreljeno: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Opombe</Label>
                  <Input
                    value={formData.opis}
                    onChange={(e) => setFormData({ ...formData, opis: e.target.value })}
                    placeholder="Dodatne opombe"
                  />
                </div>

                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Prekliči
                  </Button>
                  <Button type="submit" disabled={!editingQuota && (!selectedCategory || !selectedSubcategory)}>
                    {editingQuota ? "Posodobi" : "Dodaj"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Kvote</p>
            <p className="text-2xl font-bold">{quotas.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Planirano</p>
            <p className="text-2xl font-bold">{quotas.reduce((sum, q) => sum + q.plan, 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Odstreljeno</p>
            <p className="text-2xl font-bold">{quotas.reduce((sum, q) => sum + q.odstreljeno, 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Quotas by Category */}
      {Object.keys(groupedQuotas).length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Target className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">Ni še določenih kvot</p>
              {canManage && (
                <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Dodaj prvo kvoto
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedQuotas).map(([categoryId, category]) => {
            const totals = getCategoryTotals(category.items);
            return (
              <Card key={categoryId}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${getQuotaColor(totals.percentage)}`} />
                      <CardTitle className="text-lg">{category.naziv}</CardTitle>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-muted-foreground">
                        {totals.odstreljeno}/{totals.plan}
                      </span>
                      <Badge className={getQuotaBadge(totals.percentage).className}>
                        {totals.percentage}%
                      </Badge>
                    </div>
                  </div>
                  <div className="quota-progress h-2 mt-2">
                    <div 
                      className={`quota-progress-bar ${getQuotaColor(totals.percentage)}`}
                      style={{ width: `${totals.percentage}%` }}
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {(() => {
                    const isQuotaClosed = (q) =>
                      q.odstrel_status === "zaprto" ||
                      ((q.plan || 0) > 0 && (q.odstreljeno || 0) >= (q.plan || 0));
                    const closedItems = category.items.filter(isQuotaClosed);
                    const openItems = category.items.filter(q => !isQuotaClosed(q));
                    const isExpanded = !!showClosed[categoryId];
                    const renderQuotaRow = (quota) => {
                      const percentage = getQuotaPercentage(quota);
                      const isClosed = isQuotaClosed(quota);
                      return (
                        <div 
                          key={quota.id}
                          className={`p-3 rounded-lg border bg-card hover:shadow-sm transition-shadow ${
                            isClosed ? "border-red-400 ring-2 ring-red-300/60 animate-pulse-ring" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className={`w-2 h-2 rounded-full ${getQuotaColor(percentage)}`} />
                              <span className="font-medium truncate">{quota.podkategorija_naziv}</span>
                              {quota.odstrel_status === "zaprto" ? (
                                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Zaprto</Badge>
                              ) : quota.odstrel_status === "odprto" ? (
                                <Badge className="bg-green-600 text-[10px] px-1.5 py-0">Odprto</Badge>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <span className="font-semibold">{quota.odstreljeno}</span>
                                <span className="text-muted-foreground">/{quota.plan}</span>
                              </div>
                              <Badge variant="outline" className="text-xs w-12 justify-center">
                                {percentage}%
                              </Badge>
                              {canManage && (
                                <div className="flex gap-1">
                                  {quota.odstrel_status !== "odprto" && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={() => handleToggleStatus(quota, "odprto")}
                                      title="Odpri odstrel"
                                      data-testid={`open-quota-${quota.id}`}
                                    >
                                      <Unlock className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {quota.odstrel_status !== "zaprto" && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleToggleStatus(quota, "zaprto")}
                                      title="Zapri odstrel"
                                      data-testid={`close-quota-${quota.id}`}
                                    >
                                      <Lock className="h-3 w-3" />
                                    </Button>
                                  )}
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7"
                                    onClick={() => openEditDialog(quota)}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-destructive"
                                    onClick={() => openDeleteDialog(quota)}
                                    data-testid={`delete-quota-${quota.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                          {quota.opis && (
                            <div
                              className="mt-2 ml-5 text-xs text-muted-foreground italic border-l-2 border-primary/30 pl-2 whitespace-pre-wrap break-words"
                              data-testid={`quota-opomba-${quota.id}`}
                            >
                              {quota.opis}
                            </div>
                          )}
                        </div>
                      );
                    };
                    return (
                      <div className="grid gap-2">
                        {openItems.map(renderQuotaRow)}
                        {closedItems.length > 0 && (
                          <>
                            {!isExpanded ? (
                              <button
                                type="button"
                                onClick={() => setShowClosed(s => ({ ...s, [categoryId]: true }))}
                                className="text-xs text-red-700 hover:text-red-800 hover:bg-red-50 border border-red-200 rounded-md px-3 py-2 transition flex items-center justify-center gap-2"
                                data-testid={`show-closed-${categoryId}`}
                              >
                                <ChevronDown className="h-3.5 w-3.5" />
                                Pokaži zaključene ({closedItems.length})
                              </button>
                            ) : (
                              <>
                                {closedItems.map(renderQuotaRow)}
                                <button
                                  type="button"
                                  onClick={() => setShowClosed(s => ({ ...s, [categoryId]: false }))}
                                  className="text-xs text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-md px-3 py-2 transition flex items-center justify-center gap-2"
                                  data-testid={`hide-closed-${categoryId}`}
                                >
                                  <ChevronUp className="h-3.5 w-3.5" />
                                  Skrij zaključene
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-sm mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>Izbriši kvoto?</AlertDialogTitle>
            <AlertDialogDescription>
              Ali ste prepričani, da želite izbrisati kvoto "{quotaToDelete?.podkategorija_naziv}"? 
              Te akcije ni mogoče razveljaviti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => setQuotaToDelete(null)}>Prekliči</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(quotaToDelete?.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Izbriši
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SMS Notification Dialog */}
      <Dialog open={!!smsNotifyData} onOpenChange={(open) => !open && setSmsNotifyData(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Obvesti člane o spremembi odstrela</DialogTitle>
            <DialogDescription>
              Pošljite SMS obvestilo vsem članom preko vaše mobilne naprave.
            </DialogDescription>
          </DialogHeader>
          {smsNotifyData && (
            <div className="space-y-3">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium mb-1">Besedilo SMS:</p>
                <p className="text-muted-foreground">{smsNotifyData.sms_besedilo}</p>
              </div>
              <p className="text-xs text-muted-foreground">{smsNotifyData.phones.length} prejemnikov</p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setSmsNotifyData(null)}>Preskoči</Button>
            <a
              href={smsUrlFirst}
              onClick={() => { toast.info(`SMS 1/${smsNotifyData?.phones?.length || 1}`); setTimeout(() => setSmsNotifyData(null), 300); }}
              className="inline-flex items-center justify-center gap-1 h-10 px-4 rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 text-sm font-medium transition"
              data-testid="sms-individual-btn"
            >
              <Phone className="h-4 w-4 mr-1" />
              Posamično
            </a>
            <a
              href={smsUrlAll}
              onClick={() => { toast.success("SMS aplikacija odprta"); setTimeout(() => setSmsNotifyData(null), 300); }}
              className="inline-flex items-center justify-center gap-1 h-10 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition"
              data-testid="sms-all-btn"
            >
              <Send className="h-4 w-4 mr-1" />
              Pošlji vsem
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lovne dobe (hunting seasons) - reference info from LZS */}
      <HuntingSeasons />
    </div>
  );
};

export default QuotaPage;
